<div class="outbound-events-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>Outbound Events Integration</mat-card-title>
      <mat-card-subtitle>Configure event forwarding to Segment or Webhook for analytics and customer data platform integration</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      @if (isLoading) {
        <div class="loading-container">
          <mat-spinner diameter="50"></mat-spinner>
          <p>Loading configuration...</p>
        </div>
      }

      @if (!isLoading) {
        <form [formGroup]="configForm">
          <!-- Provider Selection -->
          <div class="form-row">
            <mat-form-field class="full-width">
              <mat-label>Provider</mat-label>
              <mat-select formControlName="provider">
                @for (provider of providers; track provider) {
                  <mat-option [value]="provider">{{provider}}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <!-- Segment Write Key -->
          @if (isProviderSegment) {
            <div class="form-row">
              <mat-form-field class="full-width">
                <mat-label>Segment Write Key</mat-label>
                <input matInput type="password" formControlName="writeKey" placeholder="Enter Segment write key">
                <mat-icon matPrefix>vpn_key</mat-icon>
                @if (configForm.get('writeKey')?.hasError('required')) {
                  <mat-error>
                    Write key is required
                  </mat-error>
                }
              </mat-form-field>
            </div>
          }
          <!-- Webhook Configuration -->
          @if (isProviderWebhook) {
            <div>
              <div class="form-row">
                <mat-form-field class="full-width">
                  <mat-label>Webhook Endpoint</mat-label>
                  <input matInput type="url" formControlName="webhookEndpoint" placeholder="https://example.com/webhook">
                  <mat-icon matPrefix>link</mat-icon>
                  @if (configForm.get('webhookEndpoint')?.hasError('required')) {
                    <mat-error>
                      Webhook endpoint is required
                    </mat-error>
                  }
                </mat-form-field>
              </div>
              <div class="form-row">
                <mat-form-field class="full-width">
                  <mat-label>Security Key</mat-label>
                  <input matInput type="password" formControlName="webhookSecurityKey" placeholder="Enter security key">
                  <mat-icon matPrefix>vpn_key</mat-icon>
                  @if (configForm.get('webhookSecurityKey')?.hasError('required')) {
                    <mat-error>
                      Security key is required
                    </mat-error>
                  }
                </mat-form-field>
              </div>
              <div class="form-row">
                <button type="button" mat-stroked-button color="accent" (click)="checkConnection()"
                  [disabled]="isCheckingConnection || !configForm.get('webhookEndpoint')?.value || !configForm.get('webhookSecurityKey')?.value">
                  @if (!isCheckingConnection) {
                    <mat-icon>wifi_tethering</mat-icon>
                  }
                  @if (isCheckingConnection) {
                    <mat-spinner diameter="20"></mat-spinner>
                  }
                  Check Connection
                </button>
              </div>
              <p class="helper-text">Select which event types should be forwarded to the provider</p>
              <mat-form-field class="full-width chips-field">
                <mat-label>Event Types</mat-label>
                <mat-chip-grid #chipGrid aria-label="Event type selection">
                  @for (eventType of selectedEventTypes; track eventType) {
                    <mat-chip-row
                      (removed)="removeEventType(eventType)"
                      [editable]="false"
                      [disabled]="isProviderNone">
                      {{eventType}}
                      <button matChipRemove [attr.aria-label]="'remove ' + eventType" [disabled]="isProviderNone">
                        <mat-icon>cancel</mat-icon>
                      </button>
                    </mat-chip-row>
                  }
                </mat-chip-grid>
                <input
                  #eventTypeInput
                  [formControl]="eventTypeControl"
                  placeholder="Type to add event types..."
                  [matChipInputFor]="chipGrid"
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  (matChipInputTokenEnd)="addEventType($event)"
                  [matAutocomplete]="auto"
                  [disabled]="isProviderNone">
                <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onEventTypeSelected($event.option.value)">
                  @for (eventType of availableEventTypes | slice:0:10; track eventType) {
                    <mat-option
                      [value]="eventType"
                      [disabled]="selectedEventTypes.includes(eventType) || isProviderNone">
                      {{eventType}}
                    </mat-option>
                  }
                </mat-autocomplete>
                <mat-hint>Available: {{availableEventTypes.length}} event types</mat-hint>
              </mat-form-field>
              @if (availableEventTypes.length > 0 && !isProviderNone) {
                <div class="event-types-list">
                  <p class="helper-text"><strong>Available Event Types:</strong></p>
                  <div class="event-type-chips">
                    @for (eventType of availableEventTypes; track eventType) {
                      <button
                        type="button"
                        mat-stroked-button
                        [disabled]="selectedEventTypes.includes(eventType)"
                        (click)="addEventTypeFromList(eventType)"
                        class="event-type-chip">
                        {{eventType}}
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          }
          <!-- Configuration Info -->
          <div class="info-section">
            <mat-icon>info</mat-icon>
            <div class="info-content">
              <strong>How it works:</strong>
              <ul>
                <li>Events matching the selected types will be forwarded to the configured provider in real-time</li>
                <li>Campaign-level configuration will override advertiser-level settings</li>
                <li>Events include user ID and all relevant event properties</li>
              </ul>
            </div>
          </div>
        </form>
      }
    </mat-card-content>

    @if (!isLoading) {
      <mat-card-actions>
        <button mat-raised-button color="primary" (click)="onSave()" [disabled]="isSaving || configForm.invalid || (!isProviderNone && selectedEventTypes.length === 0)">
          @if (!isSaving) {
            <mat-icon>save</mat-icon>
          }
          @if (isSaving) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          Save Configuration
        </button>
        <button mat-button (click)="onCancel()" [disabled]="isSaving">
          Cancel
        </button>
      </mat-card-actions>
    }
  </mat-card>
</div>
